import { clsx } from "clsx";
import { type FC, type ReactNode, useMemo } from "react";
import { formatDateTimeFromISO, HStack, Spacer, VStack } from "widgets";

import { getUserDisplayName, getUserTooltipDisplayName } from "@/modules/users/utils/user.js";

import { type ProjectActivity, type User } from "../../graphql.js";
import styles from "./ProjectActivityBody.module.css";
import { ProjectActivityMessage } from "./ProjectActivityMessage.js";

export interface IProjectActivityBodyProps {
  activity: ProjectActivity;
  users: User[];
  isCurrentUserActivity: boolean;
  action?: ReactNode;
}

export const ProjectActivityBody: FC<IProjectActivityBodyProps> = (props) => {
  const formattedActivityDate = useMemo(
    () => formatDateTimeFromISO(props.activity.created, { hideTime: false }),
    [props.activity.created],
  );
  const fullActivityDate = useMemo(
    () => formatDateTimeFromISO(props.activity.created, { full: true }),
    [props.activity.created],
  );

  return (
    <VStack
      className={clsx(styles.container, { [styles.containerSelf]: props.isCurrentUserActivity })}
      data-testid="activity-container"
    >
      <HStack data-testid="activity-headline">
        <span title={getUserTooltipDisplayName(props.activity.user)} className={styles.sender}>
          {props.isCurrentUserActivity ? "You" : getUserDisplayName(props.activity.user)}
        </span>
        <Spacer />
        <div className={styles.datetime} title={fullActivityDate}>
          {formattedActivityDate}
        </div>
      </HStack>
      <HStack>
        <div data-testid="activity-body">
          <ProjectActivityMessage activity={props.activity} users={props.users} forNotification={false} />
        </div>
        {props.action ? (
          <>
            <Spacer />
            {props.action}
          </>
        ) : null}
      </HStack>
    </VStack>
  );
};
